import { MarkdownRenderer } from '~/components/markdown-renderer';
import { cn } from '~/lib/utils';
import type { DelegationProgressView, DelegationStepStatus } from './delegation-progress';

const STEP_DOT: Record<DelegationStepStatus, string> = {
  running: 'bg-primary animate-pulse',
  done: 'bg-success',
  error: 'bg-destructive',
};

const STEP_STATUS_WORDS: Record<DelegationStepStatus, string> = {
  running: 'in progress',
  done: 'done',
  error: 'failed',
};

/** A running delegation's steps and the latest of its specialist's text, under the card's collapsed row. */
export function DelegationProgressStrip({
  progress,
  expanded,
  agentName,
  className,
}: {
  progress: DelegationProgressView;
  expanded: boolean;
  agentName?: string;
  /** Overrides the card's own padding and seam (a chat task's panel lays it out itself). */
  className?: string;
}) {
  return (
    <div
      data-testid="delegation-progress"
      className={cn('px-3 py-1.5 border-t border-border flex flex-col gap-1.5 text-xs', expanded && 'bg-muted', className)}
    >
      {progress.steps.length > 0 && (
        <ol aria-label="Specialist steps" className="flex flex-col gap-0.5">
          {progress.steps.map((step) => (
            <li
              key={step.id}
              data-testid="delegation-step"
              data-status={step.status}
              className="flex items-center gap-2 text-muted-foreground"
            >
              <span aria-hidden className={cn('size-1.5 rounded-full shrink-0', STEP_DOT[step.status])} />
              <span>{step.label}</span>
              <span className="sr-only">{STEP_STATUS_WORDS[step.status]}</span>
            </li>
          ))}
        </ol>
      )}
      {progress.text.trim() && (
        <div data-testid="delegation-text" className="flex flex-col gap-0.5 text-muted-foreground">
          {/* Streamed before Eddy checks the reply, so it is never presented as the answer. */}
          <span className="text-[length:var(--text-2xs)] text-muted-foreground/70">
            Unchecked draft from {agentName ?? 'the specialist'}
          </span>
          {/* Bottom-aligned and clipped, so the newest lines stay in view as the text grows. */}
          <div className="max-h-24 overflow-hidden flex flex-col justify-end">
            <MarkdownRenderer content={progress.text} className="text-xs" />
          </div>
        </div>
      )}
    </div>
  );
}
